import { defineStore } from "pinia";
import useISOCode from "~/composables/useISOCode";
import type { ISettingsProfileModal } from "~/types/graph-ql/ProfileTypes";
import type { InsuranceConfig } from "~/types/insurance/config";
import type { IAppConfig, IConfigCountry, IConfigCulture, IConfigCurrency, IFeature, IFeatureFlags } from "~/types/kentico/app-settings";
import type { ISitemapLocation, ISitemaps } from "~/types/kentico/sitemaps";
import { isMobile } from "~/utils/constants";
import api from "~/utils/auth/api";
import { getCookie } from "~/utils/cookie-util";

interface ISettings {
	simpleView: boolean;
	config: IAppConfig | null;
	insurance?: InsuranceConfig;
	sitemap: ISitemapLocation[] | null;
	locale: string;
	liveChatLoaded: boolean;
	liveChatLoading: boolean;
	liveChatCanceled: boolean;
	showSearch: boolean;
	menu: boolean;
	azMenu: boolean;
	topEvents: boolean;
	bigWinners: boolean;
	signalRConnected: boolean;
	personalizedMenu: string[];
	translations: {
		[x: string]: string;
	} | null;
	isNativeApp: boolean;
	showInGameHeader: boolean | undefined;
	isSynapseV2: boolean;
	appType: string;
	hasNativeLobby: boolean;
	appsFlyerData: string;
	feedsModal: boolean;
	feedsProfileModal: boolean;
	feedsFollowerModal: boolean;
	publicFeedsProfileModal: boolean | ISettingsProfileModal;
	feedsInfoModal: boolean;
	forceSingleTab: boolean;
	showTranslations: boolean;
	autoLogin: boolean;
	postModal:
		| false
		| {
				eventId: string | number | undefined;
				marketId: string | number | undefined;
				outcomeId: string | number | undefined;
				bookingCodeMeta?:
					| {
							totalOdds: number;
							winboost: { Events: number; WinBoostValue: number } | undefined;
							outcomes: number;
							bookingCode: string | undefined;
							legs?:
								| {
										marketName: string;
										eventName: string;
										outcomeName: string;
										odds: string;
										preBoost: string | undefined;
										sportId: string;
								  }[]
								| undefined;
					  }
					| undefined;
		  };
	sharedPostModal: boolean;
	isAppInitialised: boolean;
	signalRQueue: { type: "uk" | "radar"; endpoint: string; payload: unknown }[];
}

export const useSettings = defineStore({
	id: "settings",
	state: (): ISettings => ({
		simpleView: false,
		locale: "en-US",
		insurance: undefined,
		liveChatLoaded: false,
		liveChatLoading: false,
		liveChatCanceled: false,
		showSearch: false,
		signalRConnected: true,
		menu: false,
		config: null,
		sitemap: [],
		translations: null,
		azMenu: false,
		topEvents: false,
		feedsModal: false,
		bigWinners: false,
		personalizedMenu: [],
		isNativeApp: false,
		isSynapseV2: false,
		appType: "",
		hasNativeLobby: false,
		showInGameHeader: undefined,
		appsFlyerData: "",
		forceSingleTab: false,
		feedsProfileModal: false,
		publicFeedsProfileModal: false,
		feedsFollowerModal: false,
		feedsInfoModal: false,
		postModal: false,
		showTranslations: true,
		autoLogin: true,
		sharedPostModal: false,
		isAppInitialised: false,
		signalRQueue: [],
	}),
	actions: {
		setNativeAppSettings(
			isNativeApp: boolean,
			appType: string,
			hasNativeLobby: boolean,
			isSynapseV2: string,
			autoLogin: string,
			showInGameHeader?: boolean
		) {
			this.isNativeApp = isNativeApp;
			this.appType = appType;
			this.hasNativeLobby = hasNativeLobby;
			showInGameHeader === true ? (this.showInGameHeader = true) : (this.showInGameHeader = undefined);
			isSynapseV2 === "true" ? (this.isSynapseV2 = true) : (this.isSynapseV2 = false);

			if (isNativeApp && autoLogin === "false") {
				this.autoLogin = false;
			}
		},
		hasFeature(feature: string, category: "FeatureFlags" | "AppSettings" | "ResponsibleGamingSwitches" | "JackpotSettings" = "FeatureFlags") {
			try {
				if (this.config?.devConfig?.featureMatrixExclusions?.length) {
					if (this.config?.devConfig.featureMatrixExclusions.includes(feature)) return false;
				}

				const getFeatures = () => {
					if (category === "FeatureFlags") return this.config?.appSetting?.FeatureFlagsList;
					if (category === "AppSettings") return this.config?.appSetting;
					if (category === "JackpotSettings") return this.config?.appSetting?.JackpotSettings;
					if (category === "ResponsibleGamingSwitches") return this.config?.appSetting?.ResponsibleGamingSwitches;
					return false;
				};

				const features = getFeatures();

				if (!features) {
					console.warn("Feature not found");
					return false;
				}
				if (!(feature in features)) {
					console.warn(`Feature ${feature} not found in ${category}`);
					return false;
				}
				return features[feature as keyof IAppConfig["appSetting"]];
			} catch (e) {
				console.log("hasFeature Error", e);
				return false;
			}
		},
		getCategoryFeatures(category: string, featureKeys: Array<string> | null = null): IFeatureFlags | Array<IFeature> | null {
			try {
				const currentFeatureCategory = this.config?.featureFlags.find((featureFlag): boolean => featureFlag.key === category);
				if (featureKeys && currentFeatureCategory) {
					const features = currentFeatureCategory.features.filter((f) => featureKeys.includes(f.key) && f.enabled);
					return features ?? null;
				}
				return currentFeatureCategory ?? null;
			} catch (e) {
				return null;
			}
		},
		hasVertical(vertical: string) {
			return (
				!!this.config?.verticals.find((v) => v.verticalName === vertical) || !!this.config?.casinoVerticals.find((v) => v.verticalName === vertical)
			);
		},
		setInitialLocale(configLocale: string | undefined) {
			const route = useRoute();
			const { locale } = route.query;
			if (locale) {
				const localConfig = this.config?.cultures.find((cult) => cult.cultureCode === locale);
				if (!localConfig) {
					const router = useRouter();
					const copy: { [x: string]: string } = {};
					Object.assign(copy, route.query);
					delete copy["locale"];
					router.push({ query: { ...copy } });
				}
			}
			this.locale = configLocale || (locale as string) || "en-US";
			return this.locale;
		},
		setLocale(locale: string) {
			const host = useRequestURL().hostname;
			const { $trackEvent } = useNuxtApp();
			if (host?.indexOf("localhost") !== -1 || host?.indexOf("synapse") !== -1) {
				const route = useRoute();
				const copy: { [x: string]: string } = {};
				Object.assign(copy, route.query);
				delete copy["locale"];

				$trackEvent.toggleLanguage(locale);
				window.location.href =
					window.location.origin +
					window.location.pathname +
					"?locale=" +
					locale +
					`${Object.entries(copy)
						.map((x) => `&${x[0]}=${x[1]}`)
						.join("")}`;
			} else if (this.config?.cultures[0].cultureCode === locale) {
				const host = this.config?.cultures[0].url + window.location.pathname + window.location.search;
				window.location.href = host.includes("http") ? host : `https://${host}`;
			} else {
				const localConfig = this.config?.cultures.find((cult) => cult.cultureCode === locale);
				if (localConfig) {
					const host = localConfig.url.replace("www.", `${localConfig.languageCode.toLowerCase()}.`);
					$trackEvent.toggleLanguage(locale);
					window.location.href = host.includes("http") ? host : `https://${host}`;
				}
			}
		},
		async fetchTranslations() {
			this.translations = await api({
				url: `/cron/locales/{app}/{locale}`,
				type: "config",
				options: { method: "GET" },
			});
		},
		async fetchConfig() {
			const { countryISOCode } = useISOCode();
			const config = await api<IAppConfig>({
				url: `/cron/appsettings/{app}/${countryISOCode.value}`,
				type: "config",
				options: { method: "GET" },
			});
			delete config.pageLinks;
			this.config = config;
			return this.config;
		},
		async fetchSitemaps() {
			await api<ISitemaps>({
				url: `/cron/sitemaps/synapseV2/{brand}/{locale}`,
				type: "config",
				options: { method: "GET" },
			})
				.then((res) => {
					if (res.siteMap[0]) {
						this.sitemap = res.siteMap[0].data.sitemap;
					}
				})
				.catch(() => {
					this.sitemap = [];
				});
		},
	},
	getters: {
		isRadarEnabled: () => {
			const { $config } = useNuxtApp();
			const { countryISOCode } = useISOCode();
			const isSports = !!window.location.hostname.includes("sports");
			const code = isSports ? `S${countryISOCode.value}` : countryISOCode.value;
			const radarEnabledRegions = $config.public.radarEnabled.split(",");
			return radarEnabledRegions.includes(code || "");
		},
		isSportsPPC: () => {
			return !!window.location.hostname.includes("sports");
		},

		isDataFree: () => {
			const { $config } = useNuxtApp();
			const isDataFree = getCookie("isDataFree");

			return isDataFree === "true" || $config.public.dataFreeDomain.toLowerCase() === window.location.host.toLowerCase();
		},

		isHomepageAppDownloadAvailable: (state) => {
			return isMobile && !state.isNativeApp && state.config?.devConfig.appAvailable !== false && state.config?.regionCode === "TZ";
		},
		getLocale: (state): string => state.locale,
		getSitemap: (state): ISitemapLocation[] | null => state.sitemap,
		currencyCode: (state) => {
			return state.config?.currencies ? state.config.currencies[0].currencyCode : "";
		},
		getCountryConfig: (state): IConfigCountry | undefined => {
			return state.config?.countries[0];
		},
		getCultureConfig: (state): IConfigCulture | undefined => {
			return state.config?.cultures.find((x) => x.cultureCode === state.locale);
		},
		getCurrencyConfig: (state): IConfigCurrency | undefined => {
			return state.config?.currencies[0];
		},
	},
});
